/**
 * "A new version is available" prompt.
 *
 * The app polls /version.json (stamped at build time by the build-stamp plugin
 * in vite.config.ts) and compares it to the value it booted with. When they
 * differ, a deploy has happened since this tab loaded.
 *
 * Deliberately a prompt, not an automatic reload: someone could be halfway
 * through a screening form or an interview note, and throwing that away to pick
 * up a new build is a worse outcome than running the old one for another
 * minute. They refresh when they are ready.
 *
 * Checks on an interval and whenever the tab regains focus — the focus check is
 * the one that matters, since the common case is a tab left open overnight.
 */
import { useEffect, useRef, useState } from 'react';

const VERSION_URL = '/version.json';
const POLL_MS = 5 * 60 * 1000;

async function fetchBuildId(): Promise<string | null> {
  try {
    // cache: 'no-store' matters — without it the browser happily serves the
    // stamp it cached at boot and the check never fires.
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = (await res.json()) as { build?: string };
    return typeof data.build === 'string' ? data.build : null;
  } catch {
    return null;   // offline or mid-deploy — try again next tick
  }
}

export default function UpdateBanner() {
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const bootId = useRef<string | null>(null);

  useEffect(() => {
    if (!import.meta.env.PROD) return;   // dev has HMR; the stamp isn't written
    let cancelled = false;

    const check = async () => {
      const id = await fetchBuildId();
      if (cancelled || !id) return;
      if (bootId.current === null) { bootId.current = id; return; }
      if (id !== bootId.current) setStale(true);
    };

    void check();
    const timer = window.setInterval(() => void check(), POLL_MS);
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  if (!stale || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 9000, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 999, // Fixed colours, not theme tokens: --t1 inverts between light and dark, // so a themed pill flips to light-on-light in one of the two. This is a
        // notification — it should read identically in both, and it matches the
        // existing floating sidebar toggle.
        background: 'var(--text-strong)', color: 'var(--surface)', boxShadow: '0 8px 30px rgba(0,0,0,.28)', fontSize: 14, maxWidth: 'calc(100vw - 32px)' }}
    >
      <span style={{ fontWeight: 600 }}>A new version of the app is available.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{ padding: '6px 14px', borderRadius: 999, border: 'none', cursor: 'pointer', background: 'var(--pr)', color: '#fff', fontSize: 13, fontWeight: 700 }}
      >
        Refresh
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        style={{ padding: '4px 8px', borderRadius: 999, border: 'none', cursor: 'pointer', background: 'transparent', color: '#ffffff', opacity: 0.75, fontSize: 16, lineHeight: 1 }}
      >
        ×
      </button>
    </div>
  );
}
